<template>
    <div class="form-viewer">
        <div class="container">
            <div class="form-viewer__header">
                <img src="/img/logo-black.png" />
            </div>
            <div
                class="form-viewer__wrap"
                v-if="formData"
                :class="isSended ? 'thanks' : ''"
            >
                <div class="form-viewer__title">
                    {{ formData.name }}
                </div>
                <div class="form-viewer__progress" v-if="!isSended && !isError">
                    <div
                        v-for="page in Object.keys(formData.page).length"
                        class="form-viewer__progress-item"
                        :class="
                            currentPage >= page
                                ? 'form-viewer__progress-item--filled'
                                : ''
                        "
                    ></div>
                </div>
                <div class="form-viewer__body" v-if="!isSended && !isError">
                    <div
                        v-for="page in Object.keys(formData.page)"
                        v-if="Number(page) === currentPage"
                        class="page"
                    >
                        <template
                            v-for="(field, index) in sortFields(
                                formData.page[page]
                            )"
                        >
                            <TextInput
                                v-if="field.widget.template === 'text'"
                                :key="`${field.widget.key}_${index}`"
                                :name="field.widget.key"
                                :id="`${field.widget.key}__${index}`"
                                :label="field.title"
                                :required="field.required"
                                :onChange="setValue"
                                :defaultValue="values[field.widget.key]"
                                :error="errors[field.widget.key]"
                                :not_applicable="field.not_applicable"
                            />

                            <EmailInput
                                v-if="field.widget.template === 'email'"
                                :key="`${field.widget.key}_${index}`"
                                :name="field.widget.key"
                                :id="`${field.widget.key}__${index}`"
                                :label="field.title"
                                :required="field.required"
                                :onChange="setValue"
                                :defaultValue="values[field.widget.key]"
                                :error="errors[field.widget.key]"
                                :not_applicable="field.not_applicable"
                            />

                            <URLInput
                                v-if="field.widget.template === 'url'"
                                :key="`${field.widget.key}_${index}`"
                                :name="field.widget.key"
                                :id="`${field.widget.key}__${index}`"
                                :label="field.title"
                                :required="field.required"
                                :onChange="setValue"
                                :defaultValue="values[field.widget.key]"
                                :error="errors[field.widget.key]"
                                :not_applicable="field.not_applicable"
                            />

                            <NumberInput
                                v-if="field.widget.template === 'number'"
                                :key="`${field.widget.key}_${index}`"
                                :name="field.widget.key"
                                :id="`${field.widget.key}__${index}`"
                                :label="field.title"
                                :required="field.required"
                                :onChange="setValue"
                                :defaultValue="values[field.widget.key]"
                                :error="errors[field.widget.key]"
                                :not_applicable="field.not_applicable"
                            />

                            <Textarea
                                v-if="field.widget.template === 'textarea'"
                                :key="`${field.widget.key}_${index}`"
                                :name="field.widget.key"
                                :id="`${field.widget.key}__${index}`"
                                :label="field.title"
                                :required="field.required"
                                :onChange="setValue"
                                :defaultValue="values[field.widget.key]"
                                :error="errors[field.widget.key]"
                                :not_applicable="field.not_applicable"
                            />

                            <Select
                                v-if="field.widget.template === 'select'"
                                :key="`${field.widget.key}_${index}`"
                                :name="field.widget.key"
                                :id="`${field.widget.key}__${index}`"
                                :label="field.title"
                                :required="field.required"
                                :onChange="setValue"
                                :defaultValue="values[field.widget.key]"
                                :options="field.widget.options"
                                :error="errors[field.widget.key]"
                                :custom="field.custom"
                                :not_applicable="field.not_applicable"
                            />

                            <MultiSelect
                                v-if="field.widget.template === 'multiselect'"
                                :key="`${field.widget.key}_${index}`"
                                :name="field.widget.key"
                                :id="`${field.widget.key}__${index}`"
                                :label="field.title"
                                :required="field.required"
                                :onChange="setValue"
                                :defaultValue="values[field.widget.key]"
                                :options="field.widget.options"
                                :error="errors[field.widget.key]"
                                :custom="field.custom"
                                :limit="field.limit"
                                :not_applicable="field.not_applicable"
                            />
                            <CurrencyInput
                                v-if="field.widget.template === 'currency'"
                                :key="`${field.widget.key}_${index}`"
                                :name="field.widget.key"
                                :id="`${field.widget.key}__${index}`"
                                :label="field.title"
                                :required="field.required"
                                :onChange="setValue"
                                :defaultValue="values[field.widget.key]"
                                :options="field.widget.options"
                                :error="errors[field.widget.key]"
                                :custom="field.custom"
                                :limit="field.limit"
                                :not_applicable="field.not_applicable"
                            />
                            <CountryWidget
                                v-if="field.widget.template === 'address'"
                                :countries="JSON.parse(countries)"
                                :default_country="default_country"
                                :key="`${field.widget.key}_${index}`"
                                :name="field.widget.key"
                                :id="`${field.widget.key}__${index}`"
                                :label="field.title"
                                :required="field.required"
                                :onChange="setValue"
                                :defaultValue="values[field.widget.key]"
                                :error="errors[field.widget.key]"
                                :not_applicable="field.not_applicable"
                            />

                            <YearInput
                                v-if="field.widget.template === 'year'"
                                :key="`${field.widget.key}_${index}`"
                                :name="field.widget.key"
                                :id="`${field.widget.key}__${index}`"
                                :label="field.title"
                                :required="field.required"
                                :onChange="setValue"
                                :defaultValue="values[field.widget.key]"
                                :error="errors[field.widget.key]"
                                :not_applicable="field.not_applicable"
                            />
                        </template>
                    </div>
                </div>
                <div class="form-viewer__footer" v-if="!isSended && !isError">
                    <button
                        @click="onPrevPage"
                        v-if="currentPage !== 1"
                        class="btn btn--prev btn--white"
                    >
                        Previous
                    </button>
                    <button
                        @click="onNextPage"
                        class="btn btn--next btn--gray"
                        v-text="
                            currentPage >= Object.keys(formData.page).length
                                ? 'Finish'
                                : 'Next'
                        "
                    />
                </div>
                <div v-if="isSended" class="form-viewer__success">
                    <div style="margin-top: -24px">
                        Thank you!
                        <span>Your answers were successfully stored</span>
                    </div>
                </div>

                <div v-if="isError" class="form-viewer__error">
                    <div class="list-errors">
                        <ul>
                            <li v-for="error in submitErrors">
                                {{ error }}
                            </li>
                        </ul>
                    </div>

                    <div class="btn-wrap">
                        <button
                            @click="onTryAgain"
                            class="btn btn--prev btn--white"
                        >
                            Edit
                        </button>
                    </div>
                </div>

                <div class="form-viewer__loader" v-if="isLoading">
                    <clip-loader
                        :loading="isLoading"
                        :color="'#ec4657'"
                        :size="'45px'"
                    ></clip-loader>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import axios from "axios";
import ClipLoader from "vue-spinner/src/ClipLoader.vue";

import TextInput from "./components/TextInput";
import EmailInput from "./components/EmailInput";
import URLInput from "./components/URLInput";
import NumberInput from "./components/NumberInput";
import Textarea from "./components/Textarea";
import Select from "./components/Select";
import MultiSelect from "./components/Multiselect";
import CurrencyInput from "./components/CurrencyInput";
import CountryWidget from "./components/CountryWidget";
import YearInput from "./components/YearInput";

export default {
    name: "FormViewer",
    props: ["form", "countries", "default_country"],
    components: {
        TextInput,
        EmailInput,
        URLInput,
        NumberInput,
        Textarea,
        Select,
        MultiSelect,
        CurrencyInput,
        ClipLoader,
        CountryWidget,
        YearInput,
    },
    data() {
        return {
            formData: null,
            isLoading: false,
            isSended: false,
            isError: false,
            currentPage: 1,
            errors: {},
            values: {},
            urlRegex:
                /^(https?:\/\/)?(?:www\.)?[-a-zA-Z0-9@:%._\+~#=]{1,256}\.[a-zA-Z0-9()]{1,6}\b(?:[-a-zA-Z0-9()@:%_\+.~#?&\/=]*)$/,
            emailRegex: /^[^@]+@\w+(\.\w+)+\w$/,
            submitErrors: [],
        };
    },
    mounted() {
        this.formData = JSON.parse(this.form);
    },
    methods: {
        onNextPage() {
            this.errors = {};

            this.checkIsNotEmptyValues();

            if (Object.values(this.errors).length || !this.values) {
                console.error("Validation errors", this.errors);
                return;
            }

            if (Object.keys(this.formData.page).length === this.currentPage) {
                this.sendForm();
            }
            this.currentPage = this.currentPage + 1;
        },

        onPrevPage() {
            if (this.currentPage === 1) {
                return;
            }

            this.currentPage = this.currentPage - 1;
        },

        setValue(name = "", value = "") {
            if (!name) {
                return;
            }

            console.log(name, value);

            this.values[name] = value;
        },

        checkIsNotEmptyValues() {
            try {
                let formContext = this;

                const required_fields = [];
                const not_required_fields = [];

                this.formData.page[this.currentPage].forEach((field) => {
                    if (field.required) {
                        required_fields.push({
                            name: field.widget.key,
                            type: field.widget.template,
                        });
                    } else {
                        not_required_fields.push({
                            name: field.widget.key,
                            type: field.widget.template,
                        });
                    }
                });

                required_fields.forEach((fieldname) => {
                    if (fieldname.name in formContext.values) {
                        formContext.validationFields(
                            fieldname.type,
                            formContext.values[fieldname.name],
                            fieldname.name
                        );
                    } else {
                        formContext.errors[fieldname.name] = "Required";
                    }
                });

                not_required_fields.forEach((fieldname) => {
                    if (
                        fieldname.type === "email" ||
                        fieldname.type === "url" ||
                        fieldname.type === "year"
                    ) {
                        formContext.validationFields(
                            fieldname.type,
                            formContext.values[fieldname.name],
                            fieldname.name,
                            false
                        );
                    }
                });

                this.$forceUpdate();
            } catch (err) {
                console.error("checkIsNotEmptyValues error", err);
            }
        },

        validationFields(
            type = null,
            value = null,
            name = null,
            required = true
        ) {
            if (!type || !name) {
                return;
            }
            switch (type) {
                case "text":
                    if (value && !value.value) {
                        this.errors[name] = "Required";
                    } else {
                        delete this.errors[name];
                        //this.errors[name] = null;
                    }
                    break;
                case "number":
                    if (value && !value.value) {
                        this.errors[name] = "Required";
                    } else {
                        delete this.errors[name];
                    }
                    break;
                case "url":
                    if (value && !value.value && required) {
                        this.errors[name] = "Required";
                    } else if (
                        value &&
                        value.value &&
                        !this.urlRegex.test(value.value)
                    ) {
                        this.errors[name] = "Invalid URL";
                    } else {
                        delete this.errors[name];
                    }
                    break;
                case "textarea":
                    if (value && !value.value) {
                        this.errors[name] = "Required";
                    } else {
                        delete this.errors[name];
                    }
                    break;
                case "email":
                    if (value && !value.value && required) {
                        this.errors[name] = "Required";
                    } else if (
                        value &&
                        value.value &&
                        !this.emailRegex.test(value.value)
                    ) {
                        this.errors[name] = "Invalid email";
                    } else {
                        delete this.errors[name];
                    }
                    break;
                case "select":
                    if (!value) {
                        this.errors[name] = "Required";
                    } else if (value && "value" in value && "other" in value) {
                        if (!value.value && !value.other) {
                            this.errors[name] = "Required";
                        } else {
                            delete this.errors[name];
                        }
                    } else {
                        delete this.errors[name];
                    }
                    break;
                case "multiselect":
                    if (!value) {
                        this.errors[name] = "Required";
                    } else if (value && "value" in value && "other" in value) {
                        if (!value.value.length && !value.other) {
                            this.errors[name] = "Required";
                        } else {
                            delete this.errors[name];
                        }
                    } else {
                        delete this.errors[name];
                    }
                    break;
                case "address":
                    if (!value) {
                        this.errors[name] = "Required";
                    } else if (value && "country" in value) {
                        if (!value.country.value) {
                            this.errors[name] = "Required";
                        } else {
                            delete this.errors[name];
                        }
                    } else {
                        delete this.errors[name];
                    }
                    break;
                case "year":
                    if (!value && required) {
                        this.errors[name] = "Required";
                    } else if (value && "value" in value) {
                        if (!value.value && required) {
                            this.errors[name] = "Required";
                        } else if (
                            (value.value && value.value < 1900) ||
                            value.value > new Date().getFullYear()
                        ) {
                            this.errors[
                                name
                            ] = `Enter the year from 1900 and to ${new Date().getFullYear()}`;
                        } else {
                            delete this.errors[name];
                        }
                    } else {
                        delete this.errors[name];
                    }
                default:
                    break;
            }
        },

        async sendForm() {
            try {
                const data = this.formatRequestData(this.values);

                this.isLoading = true;

                await axios
                    .post(`/form/store/${this.formData.id}`, data)
                    .then((res) => {
                        this.isSended = true;
                    })
                    .catch((err) => {
                        console.error("axios sendForm error", err);
                        this.handleSubmitError(err.response);
                        this.isError = true;
                    })
                    .finally(() => {
                        this.isLoading = false;
                    });
            } catch (err) {
                console.error("sendForm error", err);
            }
        },

        formatRequestData(__values = {}) {
            try {
                const keys = Object.keys(__values),
                    values = Object.values(__values);

                if (values.length <= 0 || keys.length <= 0) {
                    return;
                }

                let newObj = {
                    form_id: this.formData.id || "",
                    entity: {},
                };

                keys.forEach((key, index) => {
                    if (key.indexOf("entity") !== -1) {
                        const keyname = key.slice(7, key.length);

                        newObj.entity[keyname] = values[index];
                    } else {
                        newObj[key] = values[index];
                    }
                });

                return newObj;
            } catch (err) {
                console.error("formatRequestData error", err);
            }
        },

        sortFields(array = []) {
            return array.slice().sort((a, b) => a.order - b.order);
        },

        handleSubmitError(errorResponse = null) {
            if (errorResponse) {
                const status = errorResponse.status;

                if (status === 422) {
                    const validationErrors = errorResponse.data.errors;

                    const orginal_keys = Object.keys(validationErrors);

                    const shortname_keys = Object.keys(validationErrors).map(
                        (k) => k.substring(0, k.indexOf("."))
                    );
                    const error_messages = Object.values(validationErrors);

                    const field_names = [];
                    for (
                        let i = 1;
                        i < Object.keys(this.formData.page).length + 1;
                        i++
                    ) {
                        let __tempPage = this.formData.page[i];

                        if (__tempPage) {
                            __tempPage.forEach((field) => {
                                if (shortname_keys.includes(field.widget.key)) {
                                    field_names.push({
                                        key: field.widget.key,
                                        name: field.title,
                                        index: shortname_keys.findIndex(
                                            (k) => k === field.widget.key
                                        ),
                                    });
                                }
                            });
                        }
                    }

                    const errorResponseMessages = [];
                    field_names.forEach((error) => {
                        errorResponseMessages.push(
                            `${error_messages[error.index][0].replace(
                                `${error.key.replace(/_/g, " ")}.value`,
                                error.name
                            )}`
                        );
                    });

                    this.submitErrors = errorResponseMessages || [];
                }

                if (status === 500) {
                    this.submitErrors = [
                        "Server return error with status 500. Please check response in browser console",
                    ];
                }
            }
        },

        onTryAgain() {
            this.isError = false;
            this.submitErrors = [];
            this.currentPage = 1;
        },
    },
};
</script>
